import { useEffect, useRef, useState } from 'react';
import { t as uiT } from '../../../utils/ui-strings';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from '../constants';

export const useIdleTimeout = (selectedLang: string) => {
  const [idleState, setIdleState] = useState<'active' | 'warning' | 'timed_out'>('active');
  const [idleNoticeMessage, setIdleNoticeMessage] = useState<string | null>(null);
  const [isSessionClosed, setIsSessionClosed] = useState<boolean>(false);
  const [lastActivityAt, setLastActivityAt] = useState<number>(() => Date.now());
  const idleWarningTimeoutRef = useRef<number | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);

  const clearIdleTimers = () => {
    if (idleWarningTimeoutRef.current !== null) {
      window.clearTimeout(idleWarningTimeoutRef.current);
      idleWarningTimeoutRef.current = null;
    }
    if (idleTimeoutRef.current !== null) {
      window.clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearIdleTimers();
    };
  }, []);

  useEffect(() => {
    const syncIdleTimers = () => {
      clearIdleTimers();

      if (!Number.isFinite(lastActivityAt)) {
        setIdleState('active');
        setIdleNoticeMessage(null);
        return;
      }

      const now = Date.now();
      const warningAtMs = lastActivityAt + IDLE_WARNING_MS;
      const timeoutAtMs = lastActivityAt + IDLE_TIMEOUT_MS;
      const warningDelayMs = warningAtMs - now;
      const timeoutDelayMs = timeoutAtMs - now;

      const triggerWarning = () => {
        setIdleState('warning');
        setIdleNoticeMessage(uiT('idle_warning', selectedLang));
      };

      const triggerTimeout = () => {
        setIdleState('timed_out');
        setIdleNoticeMessage(uiT('idle_timeout', selectedLang));
      };

      if (timeoutDelayMs <= 0) {
        triggerTimeout();
        return;
      }

      setIdleState('active');
      setIdleNoticeMessage(null);

      if (warningDelayMs <= 0) {
        triggerWarning();
      } else {
        idleWarningTimeoutRef.current = window.setTimeout(() => {
          triggerWarning();
          idleWarningTimeoutRef.current = null;
        }, warningDelayMs);
      }

      idleTimeoutRef.current = window.setTimeout(() => {
        triggerTimeout();
        idleTimeoutRef.current = null;
        if (idleWarningTimeoutRef.current !== null) {
          window.clearTimeout(idleWarningTimeoutRef.current);
          idleWarningTimeoutRef.current = null;
        }
      }, timeoutDelayMs);
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        syncIdleTimers();
      }
    };

    syncIdleTimers();
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      clearIdleTimers();
    };
  }, [lastActivityAt, selectedLang]);

  return {
    idleState,
    setIdleState,
    idleNoticeMessage,
    setIdleNoticeMessage,
    isSessionClosed,
    setIsSessionClosed,
    setLastActivityAt,
    clearIdleTimers,
  };
};
